import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  Code2, BookOpen, Zap, Shield, Brain, Eye, Leaf, Moon, Scale, Atom, Terminal, ArrowRight,
} from 'lucide-react'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'

const brandFont = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
  fontWeight: 100,
} as const

const bodyFont = {
  fontFamily: '"Helvetica Neue", sans-serif',
  fontWeight: 300,
} as const

const monoFont = {
  fontFamily: '"JetBrains Mono", "Fira Code", "Monaco", Menlo, Consolas, monospace',
  fontWeight: 400,
} as const

const sectionReveal = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.55, ease: [0.22, 1, 0.32, 1] },
} as const

const CONSTELLATION_APIS = [
  {
    icon: Brain,
    name: 'MATRIZ Reasoning API',
    path: '/v1/reasoning',
    desc: 'Explainable reasoning graphs. Trace every inference step from input to decision.',
    badge: 'stable',
  },
  {
    icon: Shield,
    name: 'Guardian Ethics API',
    path: '/v1/guardian',
    desc: 'Constitutional AI checks. Validate outputs against ethical policy before delivery.',
    badge: 'stable',
  },
  {
    icon: Eye,
    name: 'Identity (ΛiD) API',
    path: '/v1/identity',
    desc: 'Tiered authentication, passkeys, and namespace-scoped access tokens.',
    badge: 'stable',
  },
  {
    icon: BookOpen,
    name: 'Memory Fold API',
    path: '/v1/memory',
    desc: 'Symbolic memory store with cascade-safe fold architecture. 99.7% cascade prevention.',
    badge: 'stable',
  },
  {
    icon: Moon,
    name: 'Dream Engine API',
    path: '/v1/dream',
    desc: 'Oneiric synthesis — generative loops for creative and associative reasoning.',
    badge: 'beta',
  },
  {
    icon: Leaf,
    name: 'Bio Adaptation API',
    path: '/v1/bio',
    desc: 'Bio-inspired attention rhythms and adaptive signal weighting.',
    badge: 'beta',
  },
  {
    icon: Scale,
    name: 'Consciousness Bus',
    path: '/v1/bus',
    desc: 'Pub-sub context routing between Constellation nodes. Sub-250ms handoff.',
    badge: 'stable',
  },
  {
    icon: Atom,
    name: 'Quantum Compute API',
    path: '/v1/quantum',
    desc: 'Quantum-inspired superposition planning and post-quantum cryptography.',
    badge: 'experimental',
  },
]

const BADGE_STYLES: Record<string, string> = {
  stable: 'bg-vision-teal/15 text-vision-teal border-vision-teal/30',
  beta: 'bg-lambda-gold/15 text-lambda-gold border-lambda-gold/30',
  experimental: 'bg-white/10 text-white/50 border-white/20',
}

const QUICK_START_CODE = `curl -X POST https://api.lukhas.io/v1/reasoning \\
  -H "Authorization: Bearer YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"query": "Explain the trade-offs of this decision", "trace": true}'`

export default function HomePage() {
  return (
    <div className="min-h-screen bg-consciousness-deep text-awareness-silver">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-32 pb-24 px-6 relative overflow-hidden">
        {/* Ambient grid */}
        <div
          className="absolute inset-0 opacity-[0.032] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(#06b6d4 1px, transparent 1px), linear-gradient(90deg, #06b6d4 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        {/* Radial glow */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 50% 0%, rgba(13,148,136,0.13) 0%, transparent 70%)',
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-vision-teal/10 border border-vision-teal/25 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-vision-teal animate-pulse" />
              <span className="text-vision-teal text-xs tracking-[0.15em] uppercase" style={bodyFont}>
                Developer Portal
              </span>
            </div>
          </motion.div>

          <motion.h1
            className="text-5xl md:text-7xl mb-6 text-white tracking-[0.12em]"
            style={brandFont}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          >
            Build with
            <br />
            <span className="text-transparent bg-clip-text bg-vision-gradient">
              Conscious APIs
            </span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl text-white/60 max-w-2xl mx-auto mb-4 leading-relaxed"
            style={bodyFont}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
          >
            Interactive API reference and playground for the LUKHAS Constellation framework —
            reasoning, ethics, identity, and memory as first-class primitives.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col sm:flex-row gap-4 justify-center mt-10"
          >
            <Link
              to="/playground"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-vision-gradient text-white text-sm tracking-[0.06em] hover:opacity-90 transition-opacity"
              style={bodyFont}
            >
              <Zap className="w-4 h-4" />
              Try the Playground
            </Link>
            <Link
              to="/api"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full border border-vision-teal/35 text-vision-teal text-sm tracking-[0.06em] hover:border-vision-teal/60 transition-all"
              style={bodyFont}
            >
              <Code2 className="w-4 h-4" />
              Browse API Reference
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Quick-start code snippet */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div {...sectionReveal}>
            <div className="rounded-2xl border border-white/[0.09] bg-white/[0.03] overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.07] bg-white/[0.02]">
                <span className="text-xs text-vision-teal tracking-[0.1em] uppercase" style={bodyFont}>
                  cURL
                </span>
                <span className="text-white/25 text-xs mx-2">·</span>
                <span className="text-xs text-white/35 tracking-[0.06em]" style={bodyFont}>
                  Python and TypeScript SDKs available at{' '}
                  <a href="https://lukhas.dev" className="text-vision-teal/70 hover:text-vision-teal transition-colors">
                    lukhas.dev
                  </a>
                </span>
              </div>
              <pre className="p-6 text-sm text-white/75 overflow-x-auto leading-relaxed" style={monoFont}>
                <code>{QUICK_START_CODE}</code>
              </pre>
            </div>
            <p className="text-center text-xs text-white/30 mt-3" style={bodyFont}>
              Public endpoints — no auth required.{' '}
              <a href="https://lukhas.id" className="text-vision-teal hover:text-vision-cyan transition-colors">
                Get an API key for production →
              </a>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Constellation API grid */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div {...sectionReveal} className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl tracking-[0.1em] mb-4 text-white" style={brandFont}>
              Constellation <span className="text-vision-teal">APIs</span>
            </h2>
            <p className="text-lg text-white/55 max-w-2xl mx-auto" style={bodyFont}>
              Eight cognitive modules, each a first-class API. Use them independently or
              orchestrated via the Consciousness Bus.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CONSTELLATION_APIS.map(({ icon: Icon, name, path, desc, badge }, i) => (
              <motion.div
                key={name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05, ease: [0.22, 1, 0.32, 1] }}
              >
                <Link
                  to="/api"
                  className="group block h-full rounded-xl border border-white/[0.08] bg-white/[0.03] p-6 hover:border-vision-teal/30 hover:bg-white/[0.055] transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-2 rounded-lg bg-vision-teal/10">
                      <Icon className="w-5 h-5 text-vision-teal" strokeWidth={1.5} />
                    </div>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full border tracking-[0.1em] uppercase ${BADGE_STYLES[badge]}`}
                      style={bodyFont}
                    >
                      {badge}
                    </span>
                  </div>
                  <h3
                    className="text-sm text-white mb-2 leading-snug"
                    style={{ fontFamily: '"Helvetica Neue", sans-serif', fontWeight: 400 }}
                  >
                    {name}
                  </h3>
                  <p className="text-xs text-white/45 leading-relaxed mb-4" style={bodyFont}>
                    {desc}
                  </p>
                  <code
                    className="text-[11px] text-vision-teal/70 bg-vision-teal/5 px-2 py-0.5 rounded"
                    style={monoFont}
                  >
                    {path}
                  </code>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why LUKHAS APIs */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div {...sectionReveal} className="text-center mb-14">
            <h2 className="text-4xl tracking-[0.1em] mb-4 text-white" style={brandFont}>
              Designed for <span className="text-vision-teal">explainability</span>
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Terminal,
                title: 'Trace every decision',
                body: 'Every MATRIZ reasoning call returns a full reasoning graph — not just an answer. See each inference node, its inputs, and confidence score.',
              },
              {
                icon: Shield,
                title: 'Ethics by default',
                body: 'Guardian checks run inline on every API response. Policy violations surface as structured errors, not silent failures.',
              },
              {
                icon: BookOpen,
                title: 'Auditable memory',
                body: 'Memory Fold API stores symbolic context with full provenance. Every fold is timestamped, signed, and queryable.',
              },
            ].map(({ icon: Icon, title, body }) => (
              <motion.div
                key={title}
                {...sectionReveal}
                className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-8"
              >
                <Icon className="w-8 h-8 text-vision-teal mb-5" strokeWidth={1.5} />
                <h3
                  className="text-lg text-white mb-3 tracking-wide"
                  style={{ fontFamily: '"Helvetica Neue", sans-serif', fontWeight: 300 }}
                >
                  {title}
                </h3>
                <p className="text-sm text-white/50 leading-relaxed" style={bodyFont}>
                  {body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            {...sectionReveal}
            className="rounded-2xl border border-vision-teal/20 bg-vision-teal/5 p-12 text-center"
          >
            <h2 className="text-3xl md:text-4xl tracking-[0.1em] mb-4 text-white" style={brandFont}>
              Start building today
            </h2>
            <p className="text-lg text-white/55 mb-8 max-w-xl mx-auto" style={bodyFont}>
              Public endpoints are free and require no auth. Get an API key for production
              rate limits.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/playground"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-vision-gradient text-white text-sm tracking-[0.06em] hover:opacity-90 transition-opacity"
                style={bodyFont}
              >
                <Zap className="w-4 h-4" />
                Open Playground
              </Link>
              <a
                href="https://lukhas.dev"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full border border-white/20 text-white/60 text-sm tracking-[0.06em] hover:border-white/40 hover:text-white/80 transition-all"
                style={bodyFont}
              >
                <ArrowRight className="w-4 h-4" />
                Full Documentation
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <SiteFooter />
    </div>
  )
}
